/**
 * Hook for claiming pending questions after authentication.
 *
 * AIDEV-NOTE: This hook centralizes the logic for claiming guest-submitted questions
 * after the user authenticates (either existing user login or new user signup).
 *
 * Flow:
 * - Guest submits question → pending question created with device_token
 * - User authenticates → this hook claims pending questions matching device_token, or
 *   matching carried secrets together with the signed-in email
 * - For existing users: claim happens immediately in Callback
 * - For new users: claim happens after SignupFlow creates their profile
 */

import axios from 'axios';
import { logger } from '@utils/logger';
import { useQueryClient } from '@tanstack/react-query';
import { getDeviceToken } from '@utils/deviceToken';
import { getApiBaseUrl } from '../config/api';
import { isNativePlatform } from '@lib/platform';
import { questionsBaseKey } from '@lib/cache/questions';
import {
  clearClaimedPendingQuestionSecrets,
  clearNativePendingQuestionClaim,
  clearPendingQuestionClaimInTab,
  hasNativePendingQuestionClaim,
  hasPendingQuestionClaimInTab,
  readPendingQuestionClaimSecrets,
} from '@utils/pendingQuestionClaim';
import type { PendingQuestionClaimRequest, PendingQuestionClaimResponse } from '@models/api';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const parseClaimResponse = (data: unknown): PendingQuestionClaimResponse | null => {
  if (!isRecord(data)) return null;

  const claimedCount = data.claimed_count;
  const questionIds = data.question_ids;

  if (typeof claimedCount !== 'number') return null;
  if (!Array.isArray(questionIds) || !questionIds.every(id => typeof id === 'string')) return null;

  return { claimed_count: claimedCount, question_ids: questionIds };
};

interface ErrorDetailObject {
  error_code?: string;
  message?: string;
}

const parseErrorDetail = (data: unknown): string | ErrorDetailObject | undefined => {
  if (!isRecord(data)) return undefined;
  const detail = data.detail;
  if (typeof detail === 'string') return detail;
  if (!isRecord(detail)) return undefined;

  const errorCode = detail.error_code;
  const message = detail.message;
  return {
    error_code: typeof errorCode === 'string' ? errorCode : undefined,
    message: typeof message === 'string' ? message : undefined,
  };
};

/**
 * WHICH LIMB the claim actually presented, which decides what a settled claim may retire.
 *
 * `claim_secrets` is a request that named specific rows, so only those rows were attempted.
 * `device_token` is the fallback request, which attempts every row this browser wrote. Absent
 * means no request was made at all, because this context held neither limb.
 */
export type ClaimLimb = 'claim_secrets' | 'device_token';

export interface ClaimResult {
  success: boolean;
  claimedCount: number;
  questionIds: string[];
  /**
   * What the request carried. Read by `retirePendingQuestionClaim` so a settled claim clears
   * exactly what it could have published and nothing else -- never a marker for rows it never
   * presented. Carries no secret VALUES, only which kind of limb travelled.
   */
  presented?: ClaimLimb;
  isNewUser?: boolean; // true if claim failed due to USER_NOT_FOUND
  error?: string;
  /**
   * Whether the failure looks transient and the caller should keep the pending
   * marker for retry on a later sign-in. Set on non-success only; ignored on
   * success. True for 5xx / network errors / new-user (profile not yet ready);
   * false for client-side 4xx (bad input — looping won't help).
   */
  retryable?: boolean;
}

/**
 * THE ONE PRODUCTION RECORD of a failed claim, and the one place a failed
 * `ClaimResult` is built.
 *
 * `logger.error` is the only level a production build keeps (`logger.info` and
 * `logger.warn` are DEV-gated in `@utils/logger`), and `retryable` is the difference
 * between a question that will still be posted and one that is gone -- so a line
 * without it cannot tell a marker that was KEPT from one that was RETIRED. Two returns
 * used to skip this entirely: the device-token branch wrote nothing at all (and that is
 * the branch whose caller then clears the marker), and USER_NOT_FOUND wrote only a
 * DEV-gated `logger.info`. Callers can now say "the claim wrote the record" and be right
 * about every branch, which is what lets `settlePendingQuestionClaim` stay silent on the
 * retryable path instead of adding a second, quieter line for the same failure.
 *
 * A status and a reason, never a body: no question text, no address.
 */
const failedClaim = (
  reason: string,
  {
    status = null,
    retryable,
    isNewUser = false,
    presented,
  }: {
    status?: number | null;
    retryable: boolean;
    isNewUser?: boolean;
    presented?: ClaimLimb;
  },
): ClaimResult => {
  logger.error('Failed to claim pending questions:', reason, { status, retryable });
  return {
    success: false,
    claimedCount: 0,
    questionIds: [],
    error: reason,
    retryable,
    ...(isNewUser ? { isNewUser: true } : {}),
    ...(presented ? { presented } : {}),
  };
};

/**
 * Claim pending questions for the authenticated user.
 *
 * @param accessToken - Valid access token for the authenticated user
 * @returns ClaimResult with success status and claimed question details
 */
export const claimPendingQuestions = async (accessToken: string): Promise<ClaimResult> => {
  // THE TWO LIMBS THE BACKEND MATCHES ON, and each is sent only when this browsing
  // context actually has it.
  //
  // AIDEV-NOTE [MEETBALL magic-link]: a missing device token used to end the claim here,
  // and that made the CROSS-CONTEXT claim -- the only reason the magic-link carrier
  // exists -- inert in every build. `getDeviceToken` does not mint (that is
  // `getOrCreateDeviceToken`, called by the guest submission), and the token lives in
  // localStorage under the same partition as the `mb.magicLink.*` mirror, so "the mirror
  // is absent" IMPLIES "the token is absent": wherever the carrier was needed, this
  // early return fired and the POST never happened. Minting one here is NOT the fix --
  // a fresh token matches no planted row, and the bare `email == user email` limb that
  // would otherwise have caught it is deliberately gone from the backend -- so the fix
  // is to send the SECRETS, which name the rows the visitor consented to publish, and
  // to send the device token only when there is one.
  //
  // `device_token` is optional on the wire for exactly this case
  // (`PendingQuestionClaimRequest`, backend #895), which is a UNION of the two one-limb
  // shapes -- so "exactly one of the two" is a compile error to break rather than a rule
  // to remember. A body with neither can match nothing, so it is not sent at all.
  const claimSecrets = await readPendingQuestionClaimSecrets();
  // THE DEVICE TOKEN IS THE FALLBACK LIMB, NOT A COMPANION ONE, and sending both would make
  // this feature's central promise false.
  //
  // AIDEV-NOTE [MEETBALL magic-link]: the backend matches
  // `device_token == X OR (claim_secret_hash IN (...) AND lower(email) == user email)`, and
  // `meetball_device_token` is profile-wide localStorage that deliberately survives sign-out and
  // is deliberately never swept (`deviceToken.ts`, `storageCleanup.ts`). So it is shared by every
  // tab and every PERSON using that browser profile. Send it alongside consented secrets and the
  // claim publishes rows the gate never named: A asks as a guest in profile P and never signs in;
  // B, same profile, asks their own question and redeems a link; the gate names B's question, and
  // the device-token limb publishes A's row too, under B's account. That is the shared laptop,
  // the library machine and the hot desk -- the population this whole feature is easiest for.
  //
  // So the token travels ONLY when the visitor consented to nothing nameable: a row created
  // before secrets existed, or a create whose response could not be read. Then the token is the
  // only limb there is and it matches only this browser. Whenever there ARE consented secrets
  // they are the whole claim, which is what "publishes those rows and nothing else" has to mean.
  //
  // What this gives up, stated plainly: a row this browser wrote whose secret is not in the
  // committed store -- asked in another tab of the same profile, created before the deploy
  // alongside one that was not, evicted by the five-entry cap on a sixth ask in one tab, or lost
  // outright when the tab's own write fails closed (thrown, or silently discarded and caught
  // by the read-back check; on native the durable list keeps that ask for a later settle) -- is
  // no longer published by a claim that names a different question.
  //
  // AND ONE THIS LIST USED TO MISS, because the row here WAS stored: a guest who types one
  // address in the guest form and then signs in as ANOTHER matches nothing. The secret limb
  // requires `lower(row.email) == lower(user email)` (backend #895 `pending_questions.py:196-206`)
  // while the device-token limb has no email condition at all (:193-194) -- and that limb, which
  // published this row at base, is not sent once there are secrets. The endpoint answers 200 with
  // `claimed_count: 0`, the settle ladder reads that as success, the entries are retired, and
  // nothing is said to the visitor. Reachable today through the door's Google / Apple / LinkedIn
  // chips, an address corrected at the door after a typo in the form, Hide-My-Email on native,
  // and partial-auth `/signup`; `loginHint` only prefills an editable field. Whether the client
  // should fall back to the token limb when a secrets claim publishes nothing was a product
  // trade-off, and it is settled: Dexter cancelled TECH-509, with TECH-534, on 2026-09-23 and
  // accepted the loss, so the client does not fall back.
  //
  // THE FLAG IS ARMED TWO WAYS, AND NOTHING ELSE ARMS IT: a secret-less create
  // (`storeGuestPendingQuestionClaim`), or a gate promoting an already-deferred one back into it
  // once the visitor consents (`promoteDeferredPendingQuestionClaimInTab`). Either is what lets
  // a sign-in send the token-only request the visitor can accept at the unnamed-scope gate (see
  // `retirePendingQuestionClaim`, which replaces the flag with a deferred marker where one is
  // owed, never leaves it standing). That request sweeps up every unclaimed row on this device
  // token, so a stray from another tab or a cap-evicted one CAN ride along -- but only if some
  // tab on this profile has its own flag armed by something else first. Absent that, neither is
  // recovered: each sits unclaimed until its own 30-day TTL passes, silently and permanently.
  // That is still the correct side of the trade for what the gate CAN offer: it names what it
  // will publish, and nothing it cannot.
  //
  // THE SERVER ENFORCES THIS TOO. `_require_exactly_one_matching_limb` (backend #895) rejects a
  // body carrying both limbs and a body carrying neither, each with a 422 -- see
  // `test_a_body_carrying_both_is_refused_and_publishes_nothing` and
  // `test_a_body_with_neither_limb_is_rejected` in the backend's test_claim_secret.py. WHICH
  // DEPLOYMENTS HAVE IT IS A QUESTION WITH AN ANSWER, not a sentence to keep updating: read that
  // host's `/openapi.json`. A note naming a host and a tag is false the day after it is written
  // and nothing fails when it goes stale, so the rule is stated here without one.
  //
  // THE CLIENT DOES NOT LEAN ON IT EITHER, which is why the shapes below are what they are rather
  // than a warning about a release state. `PendingQuestionClaimRequest` is a union of the two
  // one-limb bodies, so a both-limbs body does not compile at all
  // (`PendingQuestionClaimRequestInvariants` is the assertion that keeps it that way); the ternary
  // BELOW sends `device_token` only when `claimSecrets.length === 0`, and the `!body` return BELOW
  // stops a neither-limb body before any request leaves. A relaxation reaching a deployment
  // WITHOUT the server rule would reopen the hole in full, so the limb is chosen once and the body
  // is built from that one choice -- never spread together from two optional fields. Where the
  // server rule does run, a breach degrades cleanly rather than crashing: FastAPI's 422 `detail`
  // is a list, which `parseErrorDetail` treats as an object with no fields, so it reads as a
  // generic non-retryable failure.
  const deviceToken = claimSecrets.length === 0 ? getDeviceToken() : null;
  const body: PendingQuestionClaimRequest | null = deviceToken
    ? { device_token: deviceToken }
    : claimSecrets.length > 0
      ? { claim_secrets: claimSecrets }
      : null;
  if (!body) {
    // The record, not a bare return: this is the branch whose caller then RETIRES the
    // marker, so a production build that logged nothing here could not tell a guest's
    // question being given up from one never attempted. Permanent by construction --
    // neither limb can appear later in this browsing context without another ask.
    return failedClaim('No device token or claim secrets available', { retryable: false });
  }

  // READ OFF THE BODY, not re-derived beside it: a second ternary over the same inputs is a second
  // chance for the two to disagree about which limb was presented, and the retire is scoped by it.
  const presented: ClaimLimb = 'device_token' in body ? 'device_token' : 'claim_secrets';
  const baseUrl = getApiBaseUrl().replace(/\/$/, '');

  try {
    logger.info('Attempting to claim pending questions...');

    const result = await axios.post(`${baseUrl}/api/v1/questions/pending/claim`, body, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    const parsed = parseClaimResponse(result.data as unknown);
    if (!parsed) {
      // NON-RETRYABLE ON PURPOSE, and this is the branch where that word is least obvious: the
      // status was 2xx, so the server COMMITTED the claim; only the body did not parse. Retrying
      // would re-present secrets for rows that are already published, and -- worse on a consent
      // surface -- it would KEEP the entries, so the next gate would NAME questions that have
      // already been posted. Treating it as settled is the honest reading of a 2xx: the scoped
      // retire drops exactly the entries this request presented and leaves what it did not --
      // the unnamed scope as a deferred marker on web, the unpresented durable entries on native
      // -- which is what a success does, because that is most likely what this was.
      return failedClaim('Unexpected response from server', {
        status: result.status,
        retryable: false,
        presented,
      });
    }

    logger.info('Claimed pending questions:', result.data);

    return {
      success: true,
      claimedCount: parsed.claimed_count,
      questionIds: parsed.question_ids,
      presented,
    };
  } catch (error) {
    const axiosError = axios.isAxiosError(error) ? error : null;
    const status = axiosError?.response?.status;
    const responseData: unknown = axiosError?.response?.data;
    const detail = parseErrorDetail(responseData);

    // AIDEV-NOTE: Debug logging to understand 404 response structure
    if (status === 404) {
      logger.debug('404 response details:', {
        status,
        data: responseData,
        detail,
        errorCode: typeof detail === 'object' ? detail.error_code : undefined,
      });
    }

    // Check if it's a USER_NOT_FOUND error (new user, profile not created yet)
    // AIDEV-NOTE: Be strict about this check - only treat as "new user" if we get
    // the specific USER_NOT_FOUND error code from the backend, not just any 404
    const isProfileNotFound =
      status === 404 && typeof detail === 'object' && detail.error_code === 'USER_NOT_FOUND';

    if (isProfileNotFound) {
      // New user: the profile is not created yet, so a later sign-in (after SignupFlow
      // has made one) is expected to succeed. This used to return after a DEV-gated
      // `logger.info` and skip the record below entirely.
      return failedClaim('USER_NOT_FOUND', {
        status,
        retryable: true,
        isNewUser: true,
        presented,
      });
    }

    // AIDEV-NOTE: If endpoint doesn't exist (404 without USER_NOT_FOUND error code),
    // or any other error, don't treat as "new user" - just report the error
    // This prevents misinterpreting a missing backend endpoint as "new user"
    const errorMessage = (() => {
      if (!axiosError) return 'Unknown error';
      if (typeof detail === 'string') return detail;
      if (typeof detail === 'object' && typeof detail.message === 'string') return detail.message;
      return axiosError.message;
    })();

    // 5xx + missing-status (network failure / no response) are transient and
    // worth keeping the marker so a later sign-in retries. Client 4xx are
    // permanent — clear the marker to avoid an indefinite loop on bad input.
    const retryable = status === undefined || status >= 500;

    return failedClaim(errorMessage, { status: status ?? null, retryable, presented });
  }
};

/**
 * Re-exported rather than moved-and-forgotten: the storage layer now lives in
 * `@utils/pendingQuestionClaim` (which is where the create site has to reach it without
 * dragging axios and react-query behind one write), and the native suites name this key
 * to tell a kept marker from a retired one.
 *
 * ONE KEY, and the list is not longer than the justification above it. `storeNativePendingQuestionClaim`
 * and the claims key were re-exported here with no importer -- the claims key is imported from
 * `@utils/pendingQuestionClaim`, and the writer is now private to that module -- and a dead
 * re-export of that writer is worse than dead code, because it keeps the base's no-argument call
 * shape reachable from the module the create site used to import it from.
 */
export { NATIVE_PENDING_QUESTION_KEY } from '@utils/pendingQuestionClaim';

/**
 * Check if there's a pending question claim action.
 * Checks sessionStorage first (fast, synchronous). On native, also checks
 * Capacitor Preferences as fallback -- covers the case where iOS killed the app
 * during auth and sessionStorage was lost but Preferences survived.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: the cross-tab mirror is what makes this true
 * in the tab a magic link opens in -- that tab's sessionStorage is empty by
 * construction, so both claim triggers (useCallbackRedirect for an existing
 * user, submitHelpers via SignupFlow for a new one) used to read false there and
 * the guest's question was never claimed to the account they just created, with
 * nothing failing anywhere. It reaches this function by HYDRATION at `/auth/link`
 * (`hydratePendingQuestionClaimFromMagicLinkMirror`), NOT by a fallback read
 * here. A fallback here ran on every callback in the app, which widened one
 * guest's claim to the whole browser profile for 30 minutes: the backend matched
 * a claim on device token OR email, so an abandoned guest question landed on the
 * next person to sign in on a shared machine. See @utils/pendingQuestionClaim.
 *
 * The per-tab read goes through `hasPendingQuestionClaimInTab`, which is guarded,
 * and that guard is what lets a blocked tab still reach the native leg below. A bare
 * `sessionStorage.getItem` THROWS on the access itself where Web Storage is refused
 * (iOS Safari "Block All Cookies", hardened mail WebViews -- the population this
 * feature is most often opened in), and that throw unwound the caller all the way to
 * its `/home` fallback: no claim, and no intent either.
 */
export const hasPendingQuestionClaim = async (): Promise<boolean> => {
  if (hasPendingQuestionClaimInTab()) {
    return true;
  }
  if (isNativePlatform()) {
    return hasNativePendingQuestionClaim();
  }
  return false;
};

/**
 * Clear the pending question claim from sessionStorage, the cross-tab
 * localStorage mirror and native Preferences. The mirror goes with the rest: it is
 * what a later `/auth/link` HYDRATES from, so leaving it would re-deliver a consumed
 * claim to the next link opened in this profile for the rest of its TTL.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: it used to say the mirror "is what
 * `hasPendingQuestionClaim` falls back to", and that has not been true since the
 * fallback was removed from the readers -- every one of them is sessionStorage-only
 * now, and the mirror reaches a tab exactly once, through `/auth/link`'s hydration.
 *
 * The first two live in `@utils/pendingQuestionClaim` so that `/auth/link` can
 * clear a claim it has named on its gate without importing this module's axios and
 * react-query graph. This function is the full clear: that plus native.
 */
export const clearPendingQuestionClaim = (): void => {
  clearPendingQuestionClaimInTab();
  if (isNativePlatform()) {
    void clearNativePendingQuestionClaim();
  }
};

/**
 * RETIRES EXACTLY WHAT A SETTLED CLAIM COULD HAVE PUBLISHED, and nothing it never presented.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: the settle ladder used to call the FULL clear on success,
 * and that silently retired rows nothing had attempted. During the transition a visitor can
 * hold both kinds of claim at once -- pre-secrets rows on this browser profile, which have only
 * the legacy flag, and secrets delivered from another context. The request carries the secrets
 * ALONE, so the pre-secrets rows were never presented, and wiping the flag on that success gave
 * them up without trying: the feature's own original same-device population, silently, for the
 * whole transition window.
 *
 * So:
 *   - a `claim_secrets` request retires the NAMED QUESTIONS IT PRESENTED and removes the flag.
 *     Where this tab was already owed an unnamed claim, a deferred marker replaces it and a later
 *     sign-in sends token-only for the visitor to decide at the unnamed-scope gate; where
 *     none was owed, there was no unnamed scope to defer. On native the durable list is the union
 *     of every ask this install made while the send is tab-first, so the rows the request did not
 *     present survive in Preferences (`retireNativePendingQuestionClaim`) rather than being wiped
 *     unattempted.
 *   - a `device_token` request retires EVERYTHING, because that request attempted every row
 *     this browser wrote -- there is nothing left for a later gate to offer.
 *   - no limb at all (this context held neither) retires everything too: nothing here can ever
 *     claim, so the marker is dead weight that would gate every future sign-in for nothing.
 *
 * Publishing the unnamed rows inside the SAME sign-in -- a second, token-only request after the
 * secrets one -- is deliberately not an option: it would publish rows the gate could not name
 * inside a flow the visitor consented to for named ones, which is the one thing this design
 * forbids.
 */
export const retirePendingQuestionClaim = (presented?: ClaimLimb): void => {
  if (presented === 'claim_secrets') {
    clearClaimedPendingQuestionSecrets();
    return;
  }
  clearPendingQuestionClaim();
};

export interface SettlePendingQuestionClaimArgs {
  /**
   * Resolves the token the claim POSTs with, and is called ONLY once there is a claim
   * to settle -- so a sign-in carrying none pays for no token at all, which is what
   * lets the native caller hand over its lazy acquirer. A `null` answer or a rejection is
   * a KEPT marker rather than a skipped step: a token that would not resolve is the most
   * transient failure there is.
   */
  getAccessToken: () => string | null | Promise<string | null>;
  claimQuestions: (accessToken: string) => Promise<ClaimResult>;
  invalidateQuestionsCache: () => void;
  /** Identifies the caller in the two lines this can write, e.g. `[useCallbackRedirect]`. */
  label: string;
}

/**
 * THE MARKER LIFECYCLE, and the only copy of it.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: `useCallbackRedirect` and `useNativePostAuth` each
 * kept their own ladder and the two had already drifted where it mattered: native
 * wrapped the claim in a `try`, web did not, so a `claimQuestions` REJECTION on web
 * unwound `performRedirect` into its `/home` fallback -- the stored intent neither
 * executed nor cleared, so the next sign-in in that tab inherited it -- while native
 * kept the marker and went on to execute the same intent. One ladder is what makes
 * `retryable` mean one thing on both platforms.
 *
 * Owns the whole decision, including whether there is anything to settle, so each
 * caller is one statement:
 *   - success                     -> retire what the request PRESENTED (see
 *                                    `retirePendingQuestionClaim`: a secrets-only claim removes
 *                                    the flag and, only where an unnamed claim was owed, writes
 *                                    a deferred marker in its place), invalidate the questions
 *                                    cache
 *   - retryable failure           -> KEEP everything for a later sign-in
 *   - permanent failure (4xx)     -> retire what the request presented; looping on bad input is
 *                                    worse than an orphaned row, but rows the request never
 *                                    named are not the ones that were bad
 *   - no token / a rejected token getter / a thrown claim
 *                                 -> KEEP it, and never propagate
 *
 * `claimPendingIfAny` in `src/screens/signup/submitHelpers.ts` runs the same lifecycle for
 * the NEW-user half and is deliberately not folded in here. The reason is its seams, not the
 * import direction: SignupFlow already imports this module at runtime, so SignupFlow calling
 * this ladder would be the existing screens-to-hooks direction. What differs is that the
 * signup copy takes EVERY collaborator by injection (the claim-pending check and the retire
 * included, which this ladder reaches as module functions), so `submitHelpers.test.ts` drives
 * it with plain fakes and no module mocks. Folding them means one of the two giving that up.
 */
export const settlePendingQuestionClaim = async ({
  getAccessToken,
  claimQuestions,
  invalidateQuestionsCache,
  label,
}: SettlePendingQuestionClaimArgs): Promise<void> => {
  if (!(await hasPendingQuestionClaim())) {
    return;
  }

  try {
    // THE TOKEN IS INSIDE THE `try`, because the getter is an injected collaborator and may be a
    // lazy acquirer that REJECTS (both raw adapters do). Awaited outside it, a rejection escaped
    // this ladder and unwound the caller into its `/home` fallback with the intent step skipped,
    // the defect this helper exists to remove (review 5330705557, R04). A rejection is kept like a
    // thrown claim, below; a `null` answer is kept here, with its own line.
    const accessToken = await getAccessToken();
    if (!accessToken) {
      logger.error(`${label} no access token for claiming pending questions; keeping the marker`);
      return;
    }

    const result = await claimQuestions(accessToken);

    if (result.success) {
      retirePendingQuestionClaim(result.presented);
      invalidateQuestionsCache();
      return;
    }

    if (!result.retryable) {
      // SCOPED for the same reason success is: a permanent failure on a secrets request says
      // those named rows are not publishable, and says nothing at all about rows the request
      // never presented. Retiring the legacy flag here would be the same silent drop, just on
      // the unhappy path.
      retirePendingQuestionClaim(result.presented);
    }
    // A RETRYABLE failure keeps the marker and says nothing further. `failedClaim` in
    // this module already wrote the one line a production build keeps, with `retryable`
    // on it, so the field record says whether the marker survived; a second line here
    // would double-count one failure at the level nobody can see.
  } catch (err) {
    // A THROW IS TRANSIENT, so the marker stays -- and it MUST NOT propagate. This is
    // the divergence above: swallowing it here is what keeps the caller's intent step
    // reachable on both platforms. A rejected token getter lands here too.
    logger.error(`${label} pending-question claim threw; keeping the marker`, err);
  }
};

/**
 * React hook for claiming pending questions with React Query integration.
 *
 * Usage:
 * ```tsx
 * const { claimQuestions, invalidateQuestionsCache } = useClaimPendingQuestions();
 *
 * // After getting access token:
 * const result = await claimQuestions(accessToken);
 * if (result.success) {
 *   invalidateQuestionsCache();
 *   navigate('/home');
 * }
 * ```
 */
export const useClaimPendingQuestions = () => {
  const queryClient = useQueryClient();

  /**
   * Claim pending questions and return the result.
   * Does NOT automatically clear the intended action - caller should handle that.
   */
  const claimQuestions = async (accessToken: string): Promise<ClaimResult> => {
    return claimPendingQuestions(accessToken);
  };

  /**
   * Invalidate the questions cache to show newly claimed questions.
   */
  const invalidateQuestionsCache = () => {
    void queryClient.invalidateQueries({ queryKey: questionsBaseKey() });
  };

  // `hasPendingQuestionClaim`, `clearPendingQuestionClaim` and `retirePendingQuestionClaim` are NOT
  // members here. No consumer ever destructured one -- the settle ladder calls the module-level
  // functions, SignupFlow imports the first and the last, and Callback the second -- so a hook
  // member was a second way to reach one function, and the one nothing pinned.
  return {
    claimQuestions,
    invalidateQuestionsCache,
  };
};
